<template>
  <div class="user-chat-container">
    <!-- Active chat windows, laid out side by side from the right -->
    <UserChatWindow
      v-for="(chat, index) in activeChats"
      :key="chat.chatId"
      :chat-id="chat.chatId"
      :customer-id="chat.customerId"
      :user-id="chat.userId"
      :customer-name="chat.customerName"
      :summary="chat.summary"
      :visible="true"
      :minimized="chat.minimized"
      :offset-right="getChatOffset(index)"
      @toggle-minimize="toggleMinimize"
      @close="closeChat"
    />

    <!-- Notification stack: grows upward from above the AI button area -->
    <transition-group
      name="notification-list"
      tag="div"
      class="notification-stack"
    >
      <CustomerReachingOutNotification
        v-for="n in reachOutNotifications"
        :key="n.notificationId"
        :notification="n"
        :accepting="acceptingId === n.notificationId"
        @accept="acceptSession"
        @dismiss="dismissNotification"
      />
    </transition-group>
  </div>
</template>

<script>
import CustomerReachingOutNotification from './CustomerReachingOutNotification.vue';
import UserChatWindow from './UserChatWindow.vue';
import SRConst from '@/helpers/Constants/SignalRConstants.js';

let nextChatId = 1;

export default {
  name: 'UserChatContainer',
  components: {
    CustomerReachingOutNotification,
    UserChatWindow
  },
  data() {
    return {
      reachOutNotifications: [],
      acceptingId: null,
      activeChats: []
    };
  },
  mounted() {
    this.$store.dispatch('NotificationStore/subscribe', [
      SRConst.EventKeys.CustomerReachingOut,
      this.onCustomerReachingOut
    ]);

    this.$store.dispatch('NotificationStore/subscribe', [
      SRConst.EventKeys.AgentAcceptedSession,
      this.onAgentAcceptedSession
    ]);
    
  },
  beforeDestroy() {
    this.$store.dispatch('NotificationStore/unsubscribe', [
      SRConst.EventKeys.CustomerReachingOut,
      this.onCustomerReachingOut
    ]);
    this.$store.dispatch('NotificationStore/unsubscribe', [
      SRConst.EventKeys.AgentAcceptedSession,
      this.onAgentAcceptedSession
    ]);
  },
  methods: {
    // ── Notification handling ────────────────────────────────
    onCustomerReachingOut(args) {
      console.log('Customer reaching out:', args);
      if (this.reachOutNotifications.some(n => n.notificationId === args.notificationId)) {
        return;
      }
      this.reachOutNotifications.push({ ...args });
    },
    onAgentAcceptedSession(args) {
      this.reachOutNotifications = this.reachOutNotifications.filter(
        n => n.userId !== args.userId
      );
    },
    async acceptSession(notification) {
      this.acceptingId = notification.notificationId;
      try {
        await this.$store.dispatch('NotificationStore/agentAccept', {
            notificationId: notification.notificationId,
            customerId: notification.customerId,
            userId: notification.userId
        });
        // Remove the notification
        this.reachOutNotifications = this.reachOutNotifications.filter(
          n => n.notificationId !== notification.notificationId
        );
        // Spawn a new chat window with the customer context
        this.spawnChat(notification);
      } catch (err) {
        console.error('Failed to accept session:', err);
      } finally {
        this.acceptingId = null;
      }
    },
    dismissNotification(notification) {
      this.reachOutNotifications = this.reachOutNotifications.filter(
        n => n.notificationId !== notification.notificationId
      );
    },

    // ── Chat window management ──────────────────────────────
    spawnChat(notification) {
      // Avoid duplicate chat for the same customer + user pair
      const exists = this.activeChats.some(
        c => c.customerId === notification.customerId && c.userId === notification.userId
      );
      if (exists) return;

      this.activeChats.push({
        chatId: nextChatId++,
        customerId: notification.customerId,
        userId: notification.userId,
        customerName: notification.customerName || 'Customer',
        summary: notification.summary || '',
        minimized: false
      });
    },
    toggleMinimize(chatId) {
      const chat = this.activeChats.find(c => c.chatId === chatId);
      if (chat) {
        chat.minimized = !chat.minimized;
      }
    },
    closeChat(chatId) {
      this.activeChats = this.activeChats.filter(c => c.chatId !== chatId);
    },

    /**
     * Calculate the right-offset for each chat window so they tile side-by-side.
     * The rightmost window sits at 36px (next to the AI tab), and each
     * subsequent window is shifted further left.
     */
    getChatOffset(index) {
      const WINDOW_WIDTH = 350; // 340 + 10 gap
      const BASE_RIGHT = 36;   // aligns with AI button area
      return BASE_RIGHT + index * WINDOW_WIDTH;
    }
  }
};
</script>

<style scoped>
.user-chat-container {
  /* No layout of its own – children are position: fixed */
  position: relative;
}

.notification-stack {
  position: fixed;
  right: 36px;
  bottom: calc(10% + 48px);
  z-index: 1001;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}

/* transition-group animations */
.notification-list-enter-active {
  animation: notifSlideIn 0.3s ease-out;
}
.notification-list-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.notification-list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.notification-list-move {
  transition: transform 0.3s ease;
}

@keyframes notifSlideIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
